/**
 * SplitPanel Component
 * Reusable split panel layout with resizable divider
 * Used by H5PEditor, ACHEditor, and AIContentGenerator
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
} from 'react';

// Minimum panel width in pixels
const DEFAULT_MIN_PANEL_WIDTH = 300;

function getWheelTargetElement(target: EventTarget | null): HTMLElement | null {
  if (target instanceof HTMLElement) return target;
  if (target instanceof Node && target.parentElement) return target.parentElement;
  return null;
}

/**
 * True if some element between start (inclusive) and scrollHost (exclusive)
 * can still scroll vertically with this wheel delta.
 */
function descendantChainCanConsumeVerticalWheel(
  start: HTMLElement,
  scrollHost: HTMLElement,
  deltaY: number,
): boolean {
  let node: HTMLElement | null = start;
  while (node && node !== scrollHost) {
    const st = window.getComputedStyle(node);
    const oy = st.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      const top = node.scrollTop;
      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      if ((deltaY < 0 && top > 0) || (deltaY > 0 && top < maxScroll - 1)) {
        return true;
      }
    }
    node = node.parentElement;
  }
  return false;
}

function findVerticalScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el;
  while (node) {
    const st = window.getComputedStyle(node);
    const oy = st.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * When this element cannot scroll further (or has no vertical overflow),
 * apply wheel delta to the nearest ancestor scroll container so the page can scroll.
 */
function attachWheelScrollChain(scrollHost: HTMLElement): () => void {
  const onWheel = (e: WheelEvent) => {
    const deltaY = e.deltaY;
    if (deltaY === 0) return;

    const start = getWheelTargetElement(e.target);
    if (!start || !scrollHost.contains(start)) return;

    if (descendantChainCanConsumeVerticalWheel(start, scrollHost, deltaY)) {
      return;
    }

    const sh = scrollHost.scrollHeight;
    const ch = scrollHost.clientHeight;
    const hasVerticalOverflow = sh > ch + 1;
    const top = scrollHost.scrollTop;
    const maxScroll = Math.max(0, sh - ch);

    const canConsumeWheel =
      hasVerticalOverflow &&
      ((deltaY < 0 && top > 0) || (deltaY > 0 && top < maxScroll - 1));

    if (canConsumeWheel) return;

    const outer = findVerticalScrollParent(scrollHost.parentElement);
    if (!outer) return;

    e.preventDefault();
    outer.scrollTop += deltaY;
  };

  scrollHost.addEventListener('wheel', onWheel, { passive: false });
  return () => scrollHost.removeEventListener('wheel', onWheel);
}

export interface SplitPanelColors {
  /** Divider background color (default: #e5e7eb) */
  dividerBackground?: string;
  /** Divider active/hover color (default: #3b82f6) */
  dividerActive?: string;
  /** Divider handle color (default: #9ca3af) */
  handleColor?: string;
  /** Divider handle active color (default: white) */
  handleActiveColor?: string;
  /** Scrollbar thumb color (default: #cbd5e1) */
  scrollbarThumb?: string;
  /** Scrollbar thumb hover color (default: #94a3b8) */
  scrollbarThumbHover?: string;
}

export interface SplitPanelProps {
  /** Left panel content */
  leftContent: ReactNode;
  /** Right panel content */
  rightContent: ReactNode;
  /** Show the right panel (default: true) */
  showRight?: boolean;
  /** Initial left panel width percentage (default: 50) */
  initialLeftWidth?: number;
  /** Minimum panel width in pixels (default: 300) */
  minPanelWidth?: number;
  /** Disable resizing - fixed split at initialLeftWidth (default: false) */
  disableResize?: boolean;
  /** CSS class prefix for styling (default: 'split-panel') */
  classPrefix?: string;
  /** Custom colors for divider and scrollbars */
  colors?: SplitPanelColors;
  /** Additional container style */
  style?: CSSProperties;
  /** Additional container className */
  className?: string;
  /** Callback when left panel width changes */
  onWidthChange?: (leftWidthPercent: number) => void;
}

const DEFAULT_COLORS: Required<SplitPanelColors> = {
  dividerBackground: '#e5e7eb',
  dividerActive: '#3b82f6',
  handleColor: '#9ca3af',
  handleActiveColor: 'white',
  scrollbarThumb: '#cbd5e1',
  scrollbarThumbHover: '#94a3b8',
};

/**
 * Custom hook for split panel resizing logic
 */
export function useSplitPanel(options: {
  initialLeftWidth?: number;
  minPanelWidth?: number;
  onWidthChange?: (leftWidthPercent: number) => void;
}) {
  const {
    initialLeftWidth = 50,
    minPanelWidth = DEFAULT_MIN_PANEL_WIDTH,
    onWidthChange,
  } = options;

  const [leftPanelWidth, setLeftPanelWidth] = useState(initialLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLeftPanelWidth(initialLeftWidth);
  }, [initialLeftWidth]);

  const handleDividerMouseDown = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      // Calculate percentage, respecting minimum widths
      const minWidthPercent = (minPanelWidth / containerWidth) * 100;
      const maxWidthPercent = 100 - minWidthPercent;

      let newLeftWidth = (mouseX / containerWidth) * 100;
      newLeftWidth = Math.max(minWidthPercent, Math.min(maxWidthPercent, newLeftWidth));

      setLeftPanelWidth(newLeftWidth);
      onWidthChange?.(newLeftWidth);
    };

    const endDrag = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);
    window.addEventListener('blur', endDrag);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('pointerup', endDrag);
      document.removeEventListener('pointercancel', endDrag);
      window.removeEventListener('blur', endDrag);
    };
  }, [isDragging, minPanelWidth, onWidthChange]);

  return {
    leftPanelWidth,
    isDragging,
    containerRef,
    handleDividerMouseDown,
    setLeftPanelWidth,
  };
}

/**
 * Resizable Divider Component
 */
export interface ResizableDividerProps {
  isDragging: boolean;
  onMouseDown: (e: MouseEvent) => void;
  colors?: SplitPanelColors;
  className?: string;
}

export const ResizableDivider: React.FC<ResizableDividerProps> = ({
  isDragging,
  onMouseDown,
  colors: colorsProp,
  className,
}) => {
  const colors = { ...DEFAULT_COLORS, ...colorsProp };
  const dividerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  const dividerStyles: CSSProperties = {
    width: '6px',
    background: isDragging ? colors.dividerActive : colors.dividerBackground,
    cursor: 'col-resize',
    flexShrink: 0,
    transition: isDragging ? 'none' : 'background 0.2s',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const handleStyles: CSSProperties = {
    width: '2px',
    height: '40px',
    background: isDragging ? colors.handleActiveColor : colors.handleColor,
    borderRadius: '1px',
    transition: isDragging ? 'none' : 'background 0.2s',
  };

  const handleMouseEnter = useCallback(() => {
    if (!isDragging && dividerRef.current && handleRef.current) {
      dividerRef.current.style.background = colors.dividerActive;
      handleRef.current.style.background = colors.handleActiveColor;
    }
  }, [isDragging, colors.dividerActive, colors.handleActiveColor]);

  const handleMouseLeave = useCallback(() => {
    if (!isDragging && dividerRef.current && handleRef.current) {
      dividerRef.current.style.background = colors.dividerBackground;
      handleRef.current.style.background = colors.handleColor;
    }
  }, [isDragging, colors.dividerBackground, colors.handleColor]);

  return (
    <div
      ref={dividerRef}
      className={className || 'split-panel-divider'}
      style={dividerStyles}
      onMouseDown={onMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div ref={handleRef} style={handleStyles} />
    </div>
  );
};

/**
 * SplitPanel Component
 * A complete split panel layout with resizable divider
 */
export const SplitPanel: React.FC<SplitPanelProps> = ({
  leftContent,
  rightContent,
  showRight = true,
  initialLeftWidth = 50,
  minPanelWidth = DEFAULT_MIN_PANEL_WIDTH,
  disableResize = false,
  classPrefix = 'split-panel',
  colors: colorsProp,
  style,
  className,
  onWidthChange,
}) => {
  const colors = { ...DEFAULT_COLORS, ...colorsProp };

  const {
    leftPanelWidth,
    isDragging,
    containerRef,
    handleDividerMouseDown,
  } = useSplitPanel({
    initialLeftWidth,
    minPanelWidth,
    onWidthChange,
  });

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cleanups: Array<() => void> = [];
    if (leftPanelRef.current) {
      cleanups.push(attachWheelScrollChain(leftPanelRef.current));
    }
    if (showRight && rightPanelRef.current) {
      cleanups.push(attachWheelScrollChain(rightPanelRef.current));
    }
    return () => {
      cleanups.forEach((detach) => detach());
    };
  }, [showRight]);

  const effectiveLeftWidth = disableResize ? initialLeftWidth : leftPanelWidth;
  const showDivider = showRight && !disableResize;

  // Generate scrollbar styles (both panels + descendants so inner scroll containers get thin scrollbar)
  const scrollbarStyles = `
    .${classPrefix}-left,
    .${classPrefix}-left *,
    .${classPrefix}-right,
    .${classPrefix}-right * {
      scrollbar-width: thin;
      scrollbar-color: ${colors.scrollbarThumb} transparent;
    }
    .${classPrefix}-left::-webkit-scrollbar,
    .${classPrefix}-left *::-webkit-scrollbar,
    .${classPrefix}-right::-webkit-scrollbar,
    .${classPrefix}-right *::-webkit-scrollbar {
      width: 6px;
    }
    .${classPrefix}-left::-webkit-scrollbar-track,
    .${classPrefix}-left *::-webkit-scrollbar-track,
    .${classPrefix}-right::-webkit-scrollbar-track,
    .${classPrefix}-right *::-webkit-scrollbar-track {
      background: transparent;
    }
    .${classPrefix}-left::-webkit-scrollbar-thumb,
    .${classPrefix}-left *::-webkit-scrollbar-thumb,
    .${classPrefix}-right::-webkit-scrollbar-thumb,
    .${classPrefix}-right *::-webkit-scrollbar-thumb {
      background-color: ${colors.scrollbarThumb};
      border-radius: 3px;
    }
    .${classPrefix}-left::-webkit-scrollbar-thumb:hover,
    .${classPrefix}-left *::-webkit-scrollbar-thumb:hover,
    .${classPrefix}-right::-webkit-scrollbar-thumb:hover,
    .${classPrefix}-right *::-webkit-scrollbar-thumb:hover {
      background-color: ${colors.scrollbarThumbHover};
    }
  `;

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div
        ref={containerRef}
        className={`${classPrefix}-container ${className || ''}`}
        style={{
          display: 'flex',
          height: '100%',
          userSelect: isDragging ? 'none' : 'auto',
          position: 'relative',
          ...style,
        }}
      >
        {/* Drag overlay - covers entire container during drag to prevent iframe from capturing events */}
        {isDragging && (
          <div
            className={`${classPrefix}-drag-overlay`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              cursor: 'col-resize',
            }}
          />
        )}

        {/* Left Panel - individual scroll */}
        <div
          ref={leftPanelRef}
          className={`${classPrefix}-left`}
          style={{
            flex: showRight ? `0 0 ${effectiveLeftWidth}%` : '1 1 100%',
            minHeight: 0,
            minWidth: 0,
            position: 'relative',
            overflowY: 'auto',
            overflowX: 'auto',
            overscrollBehaviorY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {leftContent}
        </div>

        {/* Resizable Divider - hidden when disableResize */}
        {showDivider && (
          <ResizableDivider
            isDragging={isDragging}
            onMouseDown={handleDividerMouseDown}
            colors={colors}
            className={`${classPrefix}-divider`}
          />
        )}

        {/* Right Panel - always mounted to avoid iframe remount; hidden via CSS when showRight is false */}
        <div
          ref={rightPanelRef}
          className={`${classPrefix}-right`}
          style={{
            flex: showRight ? 1 : '0 0 0px',
            minHeight: 0,
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overscrollBehaviorY: 'auto',
            ...(showRight
              ? {}
              : {
                  width: 0,
                  visibility: 'hidden' as const,
                  pointerEvents: 'none' as const,
                }),
          }}
        >
          {rightContent}
        </div>
      </div>
    </>
  );
};

export default SplitPanel;
